import type { PersistedGateCheckpointDto } from '../api/employerVerificationDashboardApi';
import { GATE_ORDER_PIPE, type GateKey } from './employerVerificationCopy';

export type GateDisplayBucket = 'fail' | 'review' | 'unavailable' | 'pass';

const STATUS_RANK: Record<string, number> = {
  FAIL: 4,
  REVIEW: 3,
  PASS: 2,
  NOT_APPLICABLE: 1,
  NOT_RUN: 0,
  UNKNOWN: 0,
};

export const INCORPORATION_UNAVAILABLE_REASON_RE =
  /incorporation date is unavailable|incorporation date not available|unable to verify tenure|temporal check skipped/i;

function isIncorporationUnavailableReason(reason: string | null | undefined): boolean {
  return INCORPORATION_UNAVAILABLE_REASON_RE.test(reason ?? '');
}

export function formatGateStepLabel(step: string): string {
  if (!step.trim()) return step;
  return step
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

function normalizeCheckpointStatus(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase() || 'UNKNOWN';
}

function statusRank(status: string): number {
  return STATUS_RANK[status] ?? 0;
}

function worseStatus(a: string, b: string): string {
  if (!a || a === 'UNKNOWN') return b;
  if (!b || b === 'UNKNOWN') return a;
  return statusRank(a) >= statusRank(b) ? a : b;
}

/**
 * Mirrors employer-verification soften policy for operator-facing display.
 * Evaluator FAIL without a live hard-stop is REVIEW, not FAIL.
 */
function softenNonHardStopDisplayStatus(
  status: string,
  isHardStop: boolean,
): string {
  const u = normalizeCheckpointStatus(status);
  if ((u === 'FAIL' || u === 'ERROR') && !isHardStop) {
    return 'REVIEW';
  }
  return u;
}

/** Resolve checkpoint status for Gate analysis and related operator UI. */
export function resolvedCheckpointDisplayStatus(
  cp: PersistedGateCheckpointDto,
): string {
  const display = normalizeCheckpointStatus(cp.status);
  const evaluation = normalizeCheckpointStatus(cp.evaluationStatus);
  const isHardStop = Boolean(cp.isHardStop);

  let effective: string;
  if (isHardStop) {
    // True hard stops: show the strictest persisted outcome.
    effective = worseStatus(display, evaluation);
  } else {
    // Soft product: honor display status; do not re-elevate via evaluationStatus.
    effective = softenNonHardStopDisplayStatus(display, false);
  }

  // Legacy fail-open rows: PASS display + unavailable incorporation reason.
  if (
    effective === 'PASS' &&
    cp.step.trim().toUpperCase() === 'INCORPORATION_DATE' &&
    isIncorporationUnavailableReason(cp.reason)
  ) {
    effective = 'REVIEW';
  }

  return effective;
}

export function gateDisplayBucketFromStatus(
  status: string | null | undefined,
): GateDisplayBucket {
  const u = normalizeCheckpointStatus(status);
  if (u === 'FAIL') return 'fail';
  if (u === 'REVIEW') return 'review';
  if (u === 'PASS') return 'pass';
  return 'unavailable';
}

export function gateDisplayBucketFromCheckpoints(
  steps: PersistedGateCheckpointDto[] | undefined,
): GateDisplayBucket | null {
  if (!steps?.length) return null;

  let worst: GateDisplayBucket = 'pass';
  for (const step of steps) {
    const bucket = gateDisplayBucketFromStatus(
      resolvedCheckpointDisplayStatus(step),
    );
    if (bucket === 'fail') return 'fail';
    if (bucket === 'review') worst = 'review';
    else if (bucket === 'unavailable' && worst === 'pass') worst = 'unavailable';
  }

  return worst;
}

/** Gate-level status for display, elevating legacy fail-open incorporation checkpoints. */
export function resolvedGateDisplayStatus(
  storedGateStatus: string | null | undefined,
  steps: PersistedGateCheckpointDto[] | undefined,
): string {
  const bucket = rollupGateDisplayBucket(storedGateStatus, steps);
  if (bucket === 'fail') return 'FAIL';
  if (bucket === 'review') return 'REVIEW';
  if (bucket === 'pass') return 'PASS';
  const stored = normalizeCheckpointStatus(storedGateStatus);
  return stored === 'UNKNOWN' ? 'NOT_RUN' : stored;
}

export function rollupGateDisplayBucket(
  storedGateStatus: string | null | undefined,
  steps: PersistedGateCheckpointDto[] | undefined,
): GateDisplayBucket {
  const gateHasHardStop = steps?.some((s) => s.isHardStop) ?? false;
  const fromStored = gateDisplayBucketFromStatus(
    softenNonHardStopDisplayStatus(storedGateStatus ?? '', gateHasHardStop),
  );
  const fromSteps = gateDisplayBucketFromCheckpoints(steps);
  if (!fromSteps) return fromStored;

  const rank: Record<GateDisplayBucket, number> = {
    fail: 4,
    review: 3,
    unavailable: 1,
    pass: 2,
  };

  return rank[fromSteps] >= rank[fromStored] ? fromSteps : fromStored;
}

export function findFirstFailedPersistedCheckpoint(
  audit: Record<string, PersistedGateCheckpointDto[]> | null | undefined,
): (PersistedGateCheckpointDto & { gate: GateKey }) | null {
  if (!audit) return null;
  for (const gate of GATE_ORDER_PIPE) {
    const steps = audit[gate];
    if (!steps?.length) continue;
    for (const s of steps) {
      if (resolvedCheckpointDisplayStatus(s) === 'FAIL') {
        return { ...s, gate };
      }
    }
  }
  return null;
}
